import { cn } from '@/utils/cn'
import { formatEffortMinutes } from '@/components/Gestores/gestorDashboardFormat'

type DailyRow = {
  date: string
  effortMinutes: number
}

function formatDailyLabel(date: string): string {
  const [, month, day] = date.split('-')
  return `${day}/${month}`
}

function formatDailyTooltip(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
  })
}

function isWeekend(date: string): boolean {
  const day = new Date(`${date}T12:00:00`).getDay()
  return day === 0 || day === 6
}

export function GestorDashboardDailyBars({ title, rows }: { title: string; rows: DailyRow[] }) {
  const max = Math.max(...rows.map((row) => row.effortMinutes), 1)

  return (
    <div className='glass-card p-4 sm:p-5'>
      <h3 className='mb-4 text-sm font-semibold text-text-primary'>{title}</h3>

      {rows.length === 0 ? (
        <p className='text-sm text-text-muted'>Sem dados no período.</p>
      ) : (
        <div className='overflow-x-auto pb-1'>
          <div
            className='flex min-w-min items-end gap-1.5 sm:gap-2'
            role='img'
            aria-label={`Gráfico de horas diárias com ${rows.length} dias`}
          >
            {rows.map((row) => {
              const heightPct = Math.max(6, (row.effortMinutes / max) * 100)
              const weekend = isWeekend(row.date)
              const valueLabel = formatEffortMinutes(row.effortMinutes)

              return (
                <div
                  key={row.date}
                  className='group flex w-10 shrink-0 flex-col items-center gap-1.5 sm:w-11'
                  title={`${formatDailyTooltip(row.date)} — ${valueLabel}`}
                >
                  <div className='flex h-40 w-full items-end rounded-lg bg-[var(--glass-border)] p-1'>
                    <div
                      className={cn(
                        'w-full rounded-md transition-colors group-hover:bg-accent',
                        weekend ? 'bg-accent/40' : 'bg-accent/80',
                      )}
                      style={{ height: `${heightPct}%` }}
                    />
                  </div>
                  <span className='text-[10px] font-medium tabular-nums text-text-muted'>
                    {formatDailyLabel(row.date)}
                  </span>
                  <span className='max-w-full truncate text-[10px] font-semibold tabular-nums text-text-primary sm:text-[11px]'>
                    {valueLabel}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
